import React from "react"
import dayjs from "dayjs"
import {
  Text,
  Box,
  Button,
  Divider,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Tooltip,
  useDisclosure,
  Link,
} from "@chakra-ui/react"
import { useMutation } from "react-query"
import { Link as RLink } from "react-router-dom"

import { humanize } from "../lib/helpers/text"
import { BikeEntity } from "../lib/types/bike"
import { TrackerEntity } from "../lib/types/tracker"
import { fetcher } from "../lib/fetcher"
import { useToast } from "../lib/hooks/useToast"
import { getLockUuidFromUrl } from "../lib/helpers/utils"

import { Confirmation } from "./Confirmation"

interface FindTracker {
  results: TrackerEntity[]
  count: number
}

interface Props {
  bike: BikeEntity
  refetch: () => void
}

export const BikeTracker: React.FC<Props> = ({ bike, refetch }) => {
  const modalProps = useDisclosure()
  const toast = useToast()
  const [trackerId, setTrackerId] = React.useState("")
  const [tracker, setTracker] = React.useState<TrackerEntity | null>()

  const { mutateAsync: updateBike, isLoading: isPairLoading } = useMutation<BikeEntity, unknown, any>(
    (data) => fetcher(`bicycles/${bike?.uuid}/`, { body: JSON.stringify(data), method: "PATCH" }),
  )

  const { mutateAsync: findTracker, isLoading: isFindingTracker } = useMutation<
    FindTracker,
    string,
    { serialNumber: string }
  >(({ serialNumber }) => fetcher(`locks/?serial_number=${serialNumber}`))

  const handleUnpairTracker = async () => {
    try {
      // Yes, "lock" refers to tracker here
      const res = await updateBike({ lock: null })
      if (res?.detail?.non_field_errors) {
        toast({ description: res?.detail?.non_field_errors[0].message, status: "error" })
      } else if (res) {
        toast({ description: "Tracker unpaired" })
        refetch()
      } else {
        toast({ description: "Error unpairing tracker", status: "error" })
      }
    } catch {
      toast({ title: "Network error", description: "Please check your internet connection", status: "error" })
    }
  }

  const handleFindTracker = async () => {
    try {
      const res = await findTracker({ serialNumber: trackerId })
      if (res && res.results?.[0]?.uuid && res.count === 1) {
        setTracker(res.results[0])
      } else {
        toast({ description: "No tracker found with that serial number", status: "error" })
      }
    } catch (error) {
      toast({ title: "Network error", description: "Please check your internet connection", status: "error" })
    }
  }

  const handlePairTracker = async () => {
    try {
      const res = await updateBike({ lock: tracker?.url })
      if (res?.detail?.non_field_errors) {
        toast({ description: res?.detail?.non_field_errors[0].message, status: "error" })
      } else if (res.detail?.axa_tracker) {
        toast({ description: "Tracker already in use", status: "error" })
      } else if (res) {
        toast({ description: "Tracker paired" })
        modalProps.onClose()
        setTracker(null)
        setTrackerId("")
        refetch()
      } else {
        toast({ description: "Error pairing tracker", status: "error" })
      }
    } catch {
      toast({ title: "Network error", description: "Please check your internet connection", status: "error" })
    }
  }

  return (
    <Stack px={6} textAlign="center" spacing={2}>
      <Text fontWeight="600">Tracker</Text>
      <Box>
        {bike.devices?.tracker ? (
          <>
            <Link
              as={RLink}
              to={`/trackers/${getLockUuidFromUrl(bike.devices.tracker.url)}`}
              fontWeight="600"
              whiteSpace="nowrap"
            >
              <Text color="gray.700" fontSize="0.8rem">
                Tracker ID: <Text as="span">{bike.devices?.tracker?.bleid}</Text>
              </Text>
              <Text color="gray.700" fontSize="0.8rem">
                Tracker paired:{" "}
                <Text as="span">
                  {bike.devices?.tracker?.paired_at
                    ? dayjs(bike.devices?.tracker?.paired_at).format("MM/DD/YYYY")
                    : "N/A"}
                </Text>
              </Text>
              <Text color="gray.700" fontSize="0.8rem">
                Manufacturer: <Text as="span">{bike.devices?.tracker?.manufacturer}</Text>
              </Text>
            </Link>
            {bike.state === "in_maintenance" ? (
              <Confirmation onSubmit={handleUnpairTracker} onSubmitText="Unpair">
                <Button mt={2}>Unpair tracker</Button>
              </Confirmation>
            ) : (
              <Tooltip label="Asset must be in maintenance to unpair">
                <Box>
                  <Button mt={2} isDisabled>
                    Unpair tracker
                  </Button>
                </Box>
              </Tooltip>
            )}
          </>
        ) : (
          <>
            <Button onClick={modalProps.onOpen}>Pair tracker</Button>
            <Modal {...modalProps} isCentered>
              <ModalOverlay zIndex={100} />
              <ModalContent>
                <ModalHeader>Tracker pairing</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                  {tracker ? (
                    <>
                      <Stack bg="gray.50" p={4} px={5} spacing={4} borderRadius="md">
                        <Flex align="center" justify="space-between">
                          <Text fontSize="0.9rem">ID</Text>
                          <Text fontSize="0.9rem" color="gray.600">
                            {tracker.bleid}
                          </Text>
                        </Flex>
                        <Divider />
                        <Flex align="center" justify="space-between">
                          <Text fontSize="0.9rem">State</Text>
                          <Text fontSize="0.9rem" color="gray.600">
                            {humanize(tracker.state)}
                          </Text>
                        </Flex>
                        <Divider />
                        <Flex align="center" justify="space-between">
                          <Text fontSize="0.9rem">Battery</Text>
                          <Text fontSize="0.9rem" color="gray.600">
                            {tracker.state_of_charge}%
                          </Text>
                        </Flex>
                      </Stack>
                      <ModalFooter px={0}>
                        <Button
                          mr={3}
                          variant="outline"
                          isDisabled={isPairLoading}
                          onClick={() => {
                            setTracker(null)
                            setTrackerId("")
                          }}
                        >
                          Find another
                        </Button>
                        <Button
                          onClick={handlePairTracker}
                          isLoading={isPairLoading}
                          isDisabled={isPairLoading}
                          colorScheme="green"
                        >
                          Confirm
                        </Button>
                      </ModalFooter>
                    </>
                  ) : (
                    <>
                      <Input
                        placeholder="Tracker ID"
                        value={trackerId}
                        onChange={(e) => setTrackerId(e.target.value)}
                      />
                      <ModalFooter px={0}>
                        <Button onClick={handleFindTracker} isLoading={isFindingTracker}>
                          Next
                        </Button>
                      </ModalFooter>
                    </>
                  )}
                </ModalBody>
              </ModalContent>
            </Modal>
          </>
        )}
      </Box>
    </Stack>
  )
}
